import { useCallback, useEffect, useState } from 'react';
import { sentenceCase } from 'change-case';
import {
  Avatar,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import Swal from 'sweetalert2';
import axios, { getErrorMessage } from 'utils/axios';
import useIsMountedRef from 'hooks/useIsMountedRef';
import CRUD from 'utils/Crud';
import { fDateTime } from 'utils/formatTime';
import AddAdminModal from 'sections/@dashboard/admin/AddAdminModal';
import AppColors from 'utils/AppColors';
import { handleSelectClick } from 'utils/commonFunc';
import Label from 'components/Label';
import Scrollbar from 'components/Scrollbar';
import Iconify from 'components/Iconify';
import { fetchAdmin, postAdmin, putAdmin, toErrorMessage } from '../../admin/api';
import { AdminErrorState, AdminNoDataState } from '../../sections/admin/shared';
import AccessControlStaffDrawer from '../../sections/admin/access-control/AccessControlStaffDrawer';
import {
  buildProfileForm,
  compareTimeValues,
  formatTimeValue,
} from '../../sections/admin/access-control/accessControlUtils';
import { AdminRouteLayout, useAsyncState } from './AdminRoutePageShared';

const TABLE_HEAD = [
  { id: 'name', label: 'Staff' },
  { id: 'email', label: 'Email' },
  { id: 'role', label: 'Legacy Role' },
  { id: 'status', label: 'Legacy Status' },
  { id: 'seen_at', label: 'Last Seen' },
  { id: 'actions', label: 'Actions' },
];

const EMPTY_REQUEST_FORM = {
  permission_code: '',
  resource_type: '',
  resource_id: '',
  reason: '',
};

export default function AccessControlAdminListPage() {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const isMountedRef = useIsMountedRef();

  const detail = useAsyncState(null);
  const roles = useAsyncState([]);
  const permissions = useAsyncState([]);

  const { state: detailState, setLoading: setDetailLoading, setData: setDetailData, setError: setDetailError } = detail;
  const { state: rolesState, setLoading: setRolesLoading, setData: setRolesData, setError: setRolesError } = roles;
  const {
    state: permissionsState,
    setLoading: setPermissionsLoading,
    setData: setPermissionsData,
    setError: setPermissionsError,
  } = permissions;

  const [userList, setUserList] = useState([]);
  const [legacyState, setLegacyState] = useState({ loading: false, loadComplete: false, error: null });
  const [selected, setSelected] = useState([]);
  const [modal, setModal] = useState({ open: false, user: {}, isEdit: false });
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState('profile');
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [profileForm, setProfileForm] = useState(buildProfileForm(null));
  const [requestForm, setRequestForm] = useState(EMPTY_REQUEST_FORM);
  const [profileErrors, setProfileErrors] = useState({});
  const [requestErrors, setRequestErrors] = useState({});
  const [submittingProfile, setSubmittingProfile] = useState(false);
  const [submittingRequest, setSubmittingRequest] = useState(false);

  const selectedStaffId = selectedAdmin?.staff_id || '';
  const roleOptions = rolesState.data || [];
  const permissionOptions = permissionsState.data || [];

  const loadAccessMeta = useCallback(async () => {
    setRolesLoading();
    setPermissionsLoading();

    try {
      const [roleResponse, permissionResponse] = await Promise.all([
        fetchAdmin('/access/roles'),
        fetchAdmin('/access/permissions'),
      ]);

      setRolesData(roleResponse.roles || []);
      setPermissionsData(permissionResponse.permissions || []);
    } catch (error) {
      const message = toErrorMessage(error);
      setRolesError(message);
      setPermissionsError(message);
    }
  }, [setPermissionsData, setPermissionsError, setPermissionsLoading, setRolesData, setRolesError, setRolesLoading]);

  const getAllAdmins = useCallback(async () => {
    setLegacyState((prev) => ({ ...prev, loading: true, loadComplete: false, error: null }));

    try {
      const response = await axios.get(CRUD.admin.list);

      if (isMountedRef.current) {
        setUserList(response.data || []);
      }

      setLegacyState({ loading: false, loadComplete: true, error: null });
    } catch (error) {
      setLegacyState({
        loading: false,
        loadComplete: false,
        error: getErrorMessage(error),
      });
    }
  }, [isMountedRef]);

  const loadStaffDetail = useCallback(async (staffId, options = {}) => {
    if (!staffId) {
      return null;
    }

    if (!options.skipLoadingState) {
      setDetailLoading();
    }

    try {
      const response = await fetchAdmin(`/access/staff/${staffId}`);
      setDetailData(response);
      return response;
    } catch (error) {
      setDetailError(toErrorMessage(error));
      throw error;
    }
  }, [setDetailData, setDetailError, setDetailLoading]);

  useEffect(() => {
    getAllAdmins();
  }, [getAllAdmins]);

  useEffect(() => {
    loadAccessMeta();
  }, [loadAccessMeta]);

  const openAccessDrawer = useCallback(async (admin) => {
    if (!admin?.staff_id) {
      return;
    }

    setSelectedAdmin(admin);
    setDrawerOpen(true);
    setDrawerTab('profile');
    setProfileDialogOpen(false);
    setRequestDialogOpen(false);
    setProfileErrors({});
    setRequestErrors({});

    try {
      await loadStaffDetail(admin.staff_id);
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    }
  }, [enqueueSnackbar, loadStaffDetail]);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setDrawerTab('profile');
    setSelectedAdmin(null);
    setProfileDialogOpen(false);
    setRequestDialogOpen(false);
    setProfileErrors({});
    setRequestErrors({});
    setProfileForm(buildProfileForm(null));
    setRequestForm(EMPTY_REQUEST_FORM);
    setDetailData(null);
  }, [setDetailData]);

  const openProfileDialog = useCallback(async () => {
    if (!selectedStaffId) {
      return;
    }

    try {
      const response = detailState.data?.profile?.staff_id === selectedStaffId
        ? detailState.data
        : await loadStaffDetail(selectedStaffId, { skipLoadingState: true });

      setProfileForm(buildProfileForm(response.profile));
      setProfileErrors({});
      setProfileDialogOpen(true);
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    }
  }, [detailState.data, enqueueSnackbar, loadStaffDetail, selectedStaffId]);

  const openRequestDialog = useCallback(() => {
    if (!selectedStaffId) {
      return;
    }

    setRequestForm(EMPTY_REQUEST_FORM);
    setRequestErrors({});
    setRequestDialogOpen(true);
  }, [selectedStaffId]);

  const validateProfileForm = useCallback(() => {
    const nextErrors = {};

    if (!profileForm.role_code) {
      nextErrors.role_code = 'Role is required.';
    }

    if (!profileForm.timezone.trim()) {
      nextErrors.timezone = 'Timezone is required.';
    }

    if (!profileForm.work_days.length) {
      nextErrors.work_days = 'Select at least one work day.';
    }

    if (!profileForm.shift_start_time) {
      nextErrors.shift_start_time = 'Shift start time is required.';
    }

    if (!profileForm.shift_end_time) {
      nextErrors.shift_end_time = 'Shift end time is required.';
    }

    if (
      profileForm.shift_start_time
      && profileForm.shift_end_time
      && !compareTimeValues(profileForm.shift_start_time, profileForm.shift_end_time)
    ) {
      nextErrors.shift_end_time = 'Shift end time must be later than shift start time.';
    }

    if (!profileForm.status) {
      nextErrors.status = 'Status is required.';
    }

    setProfileErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [profileForm]);

  const validateRequestForm = useCallback(() => {
    const nextErrors = {};

    if (!requestForm.permission_code) {
      nextErrors.permission_code = 'Permission is required.';
    }

    if (!requestForm.resource_type.trim()) {
      nextErrors.resource_type = 'Resource type is required.';
    }

    if (!requestForm.resource_id.trim()) {
      nextErrors.resource_id = 'Resource ID is required.';
    }

    if (!requestForm.reason.trim()) {
      nextErrors.reason = 'Reason is required.';
    }

    setRequestErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [requestForm]);

  const saveWorkProfile = useCallback(async () => {
    if (!selectedStaffId || !validateProfileForm()) {
      return;
    }

    setSubmittingProfile(true);

    try {
      await putAdmin(`/access/staff/${selectedStaffId}`, {
        role_code: profileForm.role_code,
        timezone: profileForm.timezone.trim(),
        work_days: profileForm.work_days,
        shift_start_time: formatTimeValue(profileForm.shift_start_time),
        shift_end_time: formatTimeValue(profileForm.shift_end_time),
        allow_after_hours: Boolean(profileForm.allow_after_hours),
        status: profileForm.status,
      });

      const refreshed = await loadStaffDetail(selectedStaffId, { skipLoadingState: true });
      setProfileForm(buildProfileForm(refreshed.profile));
      enqueueSnackbar('Work profile updated', { variant: 'success' });
      setProfileDialogOpen(false);
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    } finally {
      setSubmittingProfile(false);
    }
  }, [enqueueSnackbar, loadStaffDetail, profileForm, selectedStaffId, validateProfileForm]);

  const submitAccessRequest = useCallback(async () => {
    if (!selectedStaffId || !validateRequestForm()) {
      return;
    }

    setSubmittingRequest(true);

    try {
      await postAdmin('/access/requests', {
        staff_id: selectedStaffId,
        permission_code: requestForm.permission_code,
        resource_type: requestForm.resource_type.trim(),
        resource_id: requestForm.resource_id.trim(),
        reason: requestForm.reason.trim(),
      });

      enqueueSnackbar('Access request created', { variant: 'success' });
      setRequestDialogOpen(false);
      setDrawerTab('session-grants');
      await loadStaffDetail(selectedStaffId, { skipLoadingState: true });
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    } finally {
      setSubmittingRequest(false);
    }
  }, [enqueueSnackbar, loadStaffDetail, requestForm, selectedStaffId, validateRequestForm]);

  const removeUsers = useCallback((row) => {
    Swal.fire({
      text: 'Do you want to remove this Admin?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: AppColors.DANGER,
      confirmButtonText: 'Yes, contine',
      reverseButtons: true,
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: async () => {
        try {
          await axios.delete(`${CRUD.admin.add}/${row.staff_id}`);
          setUserList((prev) => prev.filter((item) => item.id !== row.id));
          return true;
        } catch (error) {
          Swal.showValidationMessage(`${getErrorMessage(error) || error.toString()}`);
          return false;
        }
      },
    }).then((result) => {
      if (result.value) {
        Swal.close();
      }
    });
  }, []);

  return (
    <AdminRouteLayout title="Access Control" breadcrumbTitle="Access Control">
      <Stack spacing={3}>
        <Card variant="outlined">
          <Box sx={{ p: 3 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ xs: 'flex-start', md: 'center' }}
              justifyContent="space-between"
            >
              <Stack spacing={0.5}>
                <Typography variant="h5">Admin Directory</Typography>
                <Typography variant="body2" color="text.secondary">
                  Open a staff member to inspect governed access data, update the work profile, and submit targeted elevated-access requests from one place.
                </Typography>
              </Stack>
              <Button
                variant="contained"
                onClick={() => setModal({ open: true, isEdit: false, user: {} })}
                startIcon={<Iconify icon="eva:plus-fill" />}
              >
                New Admin
              </Button>
            </Stack>
          </Box>
        </Card>

        {legacyState.loading ? (
          <Card variant="outlined">
            <Box sx={{ px: 3, py: 10, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          </Card>
        ) : null}

        {!legacyState.loading && legacyState.error ? (
          <AdminErrorState title="Admin list failed" message={legacyState.error} />
        ) : null}

        {legacyState.loadComplete ? (
          <Card variant="outlined">
            <Scrollbar>
              <TableContainer sx={{ minWidth: 960 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      {TABLE_HEAD.map((headCell) => (
                        <TableCell key={headCell.id}>{headCell.label}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userList.map((row) => {
                      const { id, first_name, last_name, role, email, status, image_url, seen_at } = row;
                      const isItemSelected = selected.includes(id);

                      return (
                        <TableRow
                          hover
                          key={id}
                          selected={isItemSelected}
                          sx={{ cursor: row.staff_id ? 'pointer' : 'default' }}
                          onClick={() => {
                            if (row.staff_id) {
                              openAccessDrawer(row);
                            }
                          }}
                        >
                          <TableCell padding="checkbox" onClick={(event) => event.stopPropagation()}>
                            <Checkbox checked={isItemSelected} onClick={() => handleSelectClick(id, selected, setSelected)} />
                          </TableCell>
                          <TableCell sx={{ minWidth: 280 }}>
                            <Stack direction="row" spacing={2} alignItems="center">
                              <Avatar alt={first_name} src={image_url} />
                              <Stack spacing={0.5}>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                  <Typography variant="subtitle2">
                                    {first_name} {last_name}
                                  </Typography>
                                  {row.is_admin ? (
                                    <Label
                                      variant={theme.palette.mode === 'light' ? 'ghost' : 'filled'}
                                      color="secondary"
                                    >
                                      Admin
                                    </Label>
                                  ) : null}
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                  {row.staff_id || 'No staff id'}
                                </Typography>
                              </Stack>
                            </Stack>
                          </TableCell>
                          <TableCell>{email}</TableCell>
                          <TableCell>{role}</TableCell>
                          <TableCell>
                            <Label
                              variant={theme.palette.mode === 'light' ? 'ghost' : 'filled'}
                              color={(status === 'inactive' && 'error') || 'success'}
                            >
                              {sentenceCase(status)}
                            </Label>
                          </TableCell>
                          <TableCell>{seen_at ? fDateTime(seen_at) : 'N/A'}</TableCell>
                          <TableCell onClick={(event) => event.stopPropagation()}>
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => openAccessDrawer(row)}
                                disabled={!row.staff_id}
                              >
                                Open Access
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                onClick={() => setModal({ open: true, user: row, isEdit: true })}
                              >
                                Edit
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={() => removeUsers(row)}
                                startIcon={<Iconify icon="eva:trash-2-outline" />}
                              >
                                Delete
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {!userList.length ? (
                      <TableRow>
                        <TableCell align="center" colSpan={TABLE_HEAD.length + 1} sx={{ py: 6 }}>
                          <AdminNoDataState
                            title="No admins"
                            description="The legacy admin resource returned no staff rows to bridge into the governed Access Control workspace."
                          />
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          </Card>
        ) : null}
      </Stack>

      <AccessControlStaffDrawer
        open={drawerOpen}
        selectedAdmin={selectedAdmin}
        selectedStaffId={selectedStaffId}
        drawerTab={drawerTab}
        onTabChange={setDrawerTab}
        onClose={closeDrawer}
        detailState={detailState}
        roleOptions={roleOptions}
        permissionOptions={permissionOptions}
        profileDialogOpen={profileDialogOpen}
        onProfileDialogClose={() => setProfileDialogOpen(false)}
        onOpenProfileDialog={openProfileDialog}
        requestDialogOpen={requestDialogOpen}
        onRequestDialogClose={() => setRequestDialogOpen(false)}
        onOpenRequestDialog={openRequestDialog}
        profileForm={profileForm}
        setProfileForm={setProfileForm}
        profileErrors={profileErrors}
        requestForm={requestForm}
        setRequestForm={setRequestForm}
        requestErrors={requestErrors}
        submittingProfile={submittingProfile}
        submittingRequest={submittingRequest}
        onSaveWorkProfile={saveWorkProfile}
        onSubmitAccessRequest={submitAccessRequest}
      />

      {modal.open ? (
        <AddAdminModal
          open={modal.open}
          user={modal.user}
          isEdit={modal.isEdit}
          handleComplete={() => {
            setModal({ open: false, user: {}, isEdit: false });
            getAllAdmins();
          }}
          handleClose={() => {
            setModal({ open: false, user: {}, isEdit: false });
          }}
        />
      ) : null}
    </AdminRouteLayout>
  );
}
